import { useRef, useEffect, ReactElement } from 'react'

import 'intersection-observer'
import Loading from '@web/components/Loading'

type UseInfiniteScrollProps = {
  checkLastElement: (...args: any[]) => boolean
  fetchMore: () => unknown | Promise<unknown>
}

export type FetchMoreTriggerProps = {
  loading?: boolean
  Loader?: ReactElement
}

const useInfiniteScroll = ({
  checkLastElement,
  fetchMore,
}: UseInfiniteScrollProps): (({ loading, Loader }: FetchMoreTriggerProps) => ReactElement) => {
  const targetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!targetRef.current) return

    const isLastElement = checkLastElement()
    if (isLastElement) return

    const observer = new IntersectionObserver(async (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries
      if (!entry.isIntersecting) return

      observer.unobserve(entry.target)
      await fetchMore()
    })

    observer.observe(targetRef.current)

    // eslint-disable-next-line consistent-return
    return () => observer?.disconnect()
  }, [checkLastElement, fetchMore])

  const FetchMoreTrigger = ({ loading, Loader = <Loading /> }: FetchMoreTriggerProps) => {
    return (
      <>
        <div style={{ visibility: 'hidden' }} ref={targetRef} />
        {loading && Loader}
      </>
    )
  }

  return FetchMoreTrigger
}

export default useInfiniteScroll
